import type { CSSProperties } from "react";

import { CcBlogPreviewCard } from "@/components/blog/CcBlogPreviewCard";
import { BLOG_CARD_GRID_IMAGE_SIZES } from "@/components/blog/blog-card-grid-config";
import type { BlogListCard } from "@/lib/blog-cards";

export type CcBlogCardGridProps = {
  posts: BlogListCard[];
  headingLevel?: "h2" | "h3";
  /** Prefix for card title element ids (`${idPrefix}-${post.id}`). */
  idPrefix: string;
  regionLabel?: string;
  /** Inclusive index through which card images use `priority` (default: none). */
  imagePriorityUntilIndex?: number;
  /** Staggered entrance when wrapped in `.cc-blog-list--reveal`. */
  revealStagger?: boolean;
};

export function CcBlogCardGrid({
  posts,
  headingLevel = "h3",
  idPrefix,
  regionLabel,
  imagePriorityUntilIndex = -1,
  revealStagger = false,
}: CcBlogCardGridProps) {
  const list = (
    <ul className="cc-blog-preview__list" role="list">
      {posts.map((post, index) => (
        <li
          key={post.id}
          className="cc-blog-preview__item"
          style={
            revealStagger
              ? ({ "--cc-blog-card-index": index } as CSSProperties)
              : undefined
          }
        >
          <CcBlogPreviewCard
            post={post}
            titleId={`${idPrefix}-${post.id}`}
            headingLevel={headingLevel}
            imageSizes={BLOG_CARD_GRID_IMAGE_SIZES}
            priority={index <= imagePriorityUntilIndex}
          />
        </li>
      ))}
    </ul>
  );

  if (regionLabel) {
    return (
      <div className="cc-blog-preview__grid" role="region" aria-label={regionLabel}>
        {list}
      </div>
    );
  }

  return <div className="cc-blog-preview__grid">{list}</div>;
}
